import API_BASE, { withApiCredentials } from "../../../api";
import {
  CATEGORY_CONFIG,
  DELIVERY_MODES,
  NOTIFICATION_LOCALES,
  isStrictTime,
} from "./notificationPreferencesConfig";

const STABLE_ERROR_CODES = new Set([
  "notification_preferences_unavailable",
  "notification_preferences_invalid",
  "notification_timezone_invalid",
  "notification_locale_invalid",
  "notification_category_invalid",
  "notification_mode_invalid",
  "notification_quiet_hours_invalid",
  "csrf_token_invalid",
]);

export class NotificationPreferencesApiError extends Error {
  constructor(code, { operation, status } = {}) {
    super(code);
    this.name = "NotificationPreferencesApiError";
    this.code = code;
    this.operation = operation;
    this.status = status;
  }
}

function authorizationHeaders(authToken) {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function validatePreferences(preferences) {
  if (!isObject(preferences)) return false;
  if (typeof preferences.pushEnabled !== "boolean") return false;
  if (typeof preferences.previewsEnabled !== "boolean") return false;
  if (!NOTIFICATION_LOCALES.includes(preferences.locale)) return false;
  if (!isObject(preferences.quietHours)) return false;
  if (typeof preferences.quietHours.enabled !== "boolean") return false;
  if (!isStrictTime(preferences.quietHours.start)) return false;
  if (!isStrictTime(preferences.quietHours.end)) return false;
  if (
    preferences.quietHours.timezone !== null &&
    typeof preferences.quietHours.timezone !== "string"
  ) {
    return false;
  }
  if (!isObject(preferences.categoryModes)) return false;
  return CATEGORY_CONFIG.every(({ id }) =>
    DELIVERY_MODES.includes(preferences.categoryModes[id])
  );
}

function validateMetadata(payload) {
  if (!Array.isArray(payload.availableModes)) return false;
  if (!Array.isArray(payload.categories)) return false;
  if (!Number.isInteger(payload.policyVersion)) return false;
  if (!DELIVERY_MODES.every((mode) => payload.availableModes.includes(mode))) {
    return false;
  }
  return CATEGORY_CONFIG.every(({ id }) => payload.categories.includes(id));
}

async function errorFromResponse(response, operation) {
  let detail = "";
  try {
    const payload = await response.json();
    detail = typeof payload?.detail === "string" ? payload.detail : "";
  } catch {
    detail = "";
  }
  const fallback =
    operation === "load"
      ? "notification_preferences_load_error"
      : "notification_preferences_save_error";
  return new NotificationPreferencesApiError(
    STABLE_ERROR_CODES.has(detail) ? detail : fallback,
    { operation, status: response.status }
  );
}

function invalidResponse(operation) {
  return new NotificationPreferencesApiError(
    operation === "load"
      ? "notification_preferences_load_error"
      : "notification_preferences_save_error",
    { operation }
  );
}

export async function fetchNotificationPreferences({ authToken, signal } = {}) {
  const response = await fetch(
    `${API_BASE}/notification-preferences`,
    withApiCredentials({ headers: authorizationHeaders(authToken), signal })
  );
  if (!response.ok) throw await errorFromResponse(response, "load");
  const payload = await response.json();
  if (
    !isObject(payload) ||
    !validatePreferences(payload.preferences) ||
    !validateMetadata(payload)
  ) {
    throw invalidResponse("load");
  }
  return payload;
}
function safePatch(patch) {
  if (!isObject(patch)) return {};
  const safe = {};
  if (typeof patch.previewsEnabled === "boolean") {
    safe.previewsEnabled = patch.previewsEnabled;
  }
  if (NOTIFICATION_LOCALES.includes(patch.locale)) safe.locale = patch.locale;
  if (isObject(patch.quietHours)) {
    const quietHours = {};
    if (typeof patch.quietHours.enabled === "boolean") {
      quietHours.enabled = patch.quietHours.enabled;
    }
    for (const field of ["start", "end"]) {
      if (typeof patch.quietHours[field] === "string") {
        quietHours[field] = patch.quietHours[field];
      }
    }
    if (
      patch.quietHours.timezone === null ||
      typeof patch.quietHours.timezone === "string"
    ) {
      quietHours.timezone = patch.quietHours.timezone;
    }
    if (Object.keys(quietHours).length) safe.quietHours = quietHours;
  }
  if (isObject(patch.categoryModes)) {
    const categoryModes = {};
    for (const { id } of CATEGORY_CONFIG) {
      if (DELIVERY_MODES.includes(patch.categoryModes[id])) {
        categoryModes[id] = patch.categoryModes[id];
      }
    }
    if (Object.keys(categoryModes).length) safe.categoryModes = categoryModes;
  }
  return safe;
}

export async function patchNotificationPreferences(
  patch,
  { authToken, signal } = {}
) {
  const body = safePatch(patch);
  const response = await fetch(
    `${API_BASE}/notification-preferences`,
    withApiCredentials({
      method: "PATCH",
      headers: {
        ...authorizationHeaders(authToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    })
  );
  if (!response.ok) throw await errorFromResponse(response, "save");
  const payload = await response.json();
  if (
    !isObject(payload) ||
    payload.ok !== true ||
    !validatePreferences(payload.preferences) ||
    !Number.isInteger(payload.policyVersion)
  ) {
    throw invalidResponse("save");
  }
  return payload;
}
